import differenceInMinutes from 'date-fns/differenceInMinutes';
import { decode } from 'jsonwebtoken';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';

import {
  logoutUser,
  refreshUserToken,
} from '../../store/actions/login/loginActions';
import { JWT_TOKEN } from '../../constants/localStorage';
import { authScopeStringGetHelper } from '../helpers/authScopeHelpers';

const INACTIVE_INTERVAL = 240000;
const DEBOUNCE_TIME = 500;
const REFRESH_TIMEOUT = 60000;
const INACTIVE_ALERT_POPUP_TIMOUT = 1080000;
const EXPIRE_TIME_TOKEN_TRIGGER = 10;

// `disabled` is set while the app is showing the maintenance page. Without it
// the idle machinery keeps running behind that page: it dispatches
// refreshUserToken() at the backend that is down for the window, then logs the
// user out after ~19 minutes and pops RefreshModal over the maintenance page.
export default function useRefreshHook(
  showAlert,
  alertActive,
  disabled = false,
) {
  const [inactiveTimerId, setInactiveTimerId] = useState(null);
  const [timerId, setTimerId] = useState(null);
  const dispatch = useDispatch();

  // The idle chain runs on ~19 minutes of nested setTimeouts, so those
  // callbacks close over whatever `disabled` was when the chain started. A ref
  // is the only way for them to see a window that began after that point.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const showInactiveAlert = () => {
    if (disabledRef.current) {
      return;
    }
    showAlert(true);
  };

  const clearTimeouts = () => {
    clearTimeout(timerId);
    setTimerId(null);
    clearTimeout(inactiveTimerId);
    setInactiveTimerId(null);
  };

  // One minute time to do an action
  const startIdleCountdown = () =>
    setTimerId(
      setTimeout(() => {
        // Read through the ref: the window may have started after this
        // countdown began, and the closure would otherwise be ~19 min stale.
        if (disabledRef.current) {
          return;
        }
        dispatch(logoutUser({}));
        showAlert(false);
      }, REFRESH_TIMEOUT),
    );

  const startIdleAlertShowCountdown = () =>
    setInactiveTimerId(
      setTimeout(() => {
        showInactiveAlert();
        startIdleCountdown();
      }, INACTIVE_ALERT_POPUP_TIMOUT),
    );

  // A chain started before the window must not survive into it.
  useEffect(() => {
    if (disabled) {
      clearTimeout(timerId);
      clearTimeout(inactiveTimerId);
      setTimerId(null);
      setInactiveTimerId(null);
      showAlert(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Idle callback
  const handleOnIdle = () => {
    if (disabled) {
      return;
    }

    const JwtToken = getToken();

    if (JwtToken) {
      dispatch(refreshUserToken());
      startIdleAlertShowCountdown();
    }
  };

  // Active callback (from idle to active state). Harmless either way while
  // disabled - it only clears already-inert timer refs, never dispatches -
  // but guarded for consistency with every other callback here.
  const handleOnActive = () => {
    if (disabled) {
      return;
    }

    const JwtToken = getToken();
    if (JwtToken) {
      if (timerId || inactiveTimerId) {
        clearTimeouts();
      }
    }
  };

  // Every action callback (mouse move, keyboard stroke, etc.)
  const handleOnAction = () => {
    if (disabled) {
      return;
    }

    const JwtToken = getToken();
    const userLoggedIn = decode(JwtToken);

    if (userLoggedIn) {
      const timeUntilSessionExpire = differenceInMinutes(
        new Date(decode(JwtToken).exp * 1000),
        new Date(),
      );

      if (timeUntilSessionExpire < EXPIRE_TIME_TOKEN_TRIGGER && !alertActive) {
        dispatch(refreshUserToken());

        if (timerId || inactiveTimerId) {
          clearTimeouts();
        }
      }
    }
  };

  const { getRemainingTime } = useIdleTimer({
    timeout: INACTIVE_INTERVAL,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: DEBOUNCE_TIME,
  });

  return getRemainingTime;
}

function getToken() {
  return authScopeStringGetHelper(JWT_TOKEN);
}
